// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * This module provides a form component for managing an organization's basic details,
 * including its name and operating timezone. These settings affect how dates and times
 * are displayed throughout the application.
 */
import * as z from 'zod';
import { SparseOrganization } from '@/account/api/types';
import { UpdateOrganizationParameters } from '@/account/api/update-organization';
import { useUpdateOrganization } from '@/account/hooks/use-update-organization';
import { FormCard } from '@/components/form-card';
import { FormCardRow } from '@/components/form-card-row';
import { FormTextField } from '@/components/form-text-field';
import { FormTimezoneSearch } from '@/components/form-timezone-search';
import { SupportSnackbar } from '@/components/support-snackbar';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box } from '@mui/material';
import { Zone } from 'luxon';
import { SubmitHandler, useForm } from 'react-hook-form';

/**
 * Schema for validating the organization name.
 * Ensures a non-empty string is provided.
 */
const nameSchema = z.string().min(1, 'Name is required');

/**
 * Schema for validating the timezone selection.
 * Ensures a valid IANA timezone is provided.
 */
const timezoneSchema = z.custom<Zone>((value) => value instanceof Zone, {
  message: 'Timezone is required',
});

/**
 * Combined schema for validating the entire form.
 * Requires both name and timezone to be valid.
 */
const formSchema = z.object({ name: nameSchema, timezone: timezoneSchema });

/** Type representing the validated form data */
type FormData = z.infer<typeof formSchema>;

interface Props {
  /**
   * The organization to update.
   */
  organization: SparseOrganization;
}

/**
 * A form component that allows users to update their organization's basic details.
 * Provides fields for updating the organization name and operating timezone.
 *
 * Changes are saved only when explicitly submitted, and the form tracks dirty
 * state to enable/disable the save button appropriately. Error states are
 * displayed using a snackbar notification.
 */
export function OrganizationDetailsCard(props: Props) {
  const updateOrganizationMutation = useUpdateOrganization();

  const { control, formState, reset, handleSubmit } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: props.organization.name,
      timezone: props.organization.timezone,
    },
  });

  const isSaveDisabled =
    updateOrganizationMutation.isPending || !formState.isDirty;

  /**
   * Resets the form to its initial state, discarding any changes.
   */
  const handleReset = () => {
    reset();
  };

  /**
   * Handles form submission when the data is valid.
   * Only sends changed fields to the API to minimize unnecessary updates.
   */
  const handleValidSubmission: SubmitHandler<FormData> = (data) => {
    const parameters: UpdateOrganizationParameters = {};

    if (formState.dirtyFields.name) {
      parameters.name = data.name;
    }

    if (formState.dirtyFields.timezone) {
      parameters.timezone = data.timezone.name;
    }

    updateOrganizationMutation.mutate(
      { orgKey: props.organization.key, parameters },
      {
        onSuccess: (response) => {
          reset({
            name: response.organization.name,
            timezone: response.organization.timezone,
          });
        },
      }
    );
  };

  return (
    <FormCard
      title="Details"
      isDirty={formState.isDirty}
      isDisabled={isSaveDisabled}
      onReset={handleReset}
      onSubmit={handleSubmit(handleValidSubmission)}
    >
      <SupportSnackbar
        isOpen={updateOrganizationMutation.isError}
        message="An error occurred while updating the organization."
        onClose={updateOrganizationMutation.reset}
      />
      <FormCardRow title="Name" subtitle="Your organization's name">
        <FormTextField
          name="name"
          label="Name"
          control={control}
          disabled={updateOrganizationMutation.isPending}
          sx={{ width: { xs: '100%', sm: 300 } }}
        />
      </FormCardRow>
      <FormCardRow
        title="Timezone"
        subtitle="The timezone in which your organization operates"
      >
        <Box width={{ xs: '100%', sm: 300 }}>
          <FormTimezoneSearch
            name="timezone"
            label="Timezone"
            control={control}
            disabled={updateOrganizationMutation.isPending}
          />
        </Box>
      </FormCardRow>
    </FormCard>
  );
}
